import { FC, forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react'
import SearchIcon from '@mui/icons-material/Search'
import Button from '@mui/material/Button'
import OutlinedInput from '@mui/material/OutlinedInput'
import { AutocompleteSuggestion } from '../../../services/addressService'
import Box from '@mui/material/Box'
import useCallbackDebounced from '../../../hooks/shared/useCallbackDebounced'
import Popper from '@mui/material/Popper'
import Typography from '@mui/material/Typography'
import Backdrop from '@mui/material/Backdrop'
import LinearProgress from '../LinearProgress'

const SuggestedResultRow: FC<{
  suggestion: AutocompleteSuggestion
  selectSuggestion: (suggestion: AutocompleteSuggestion) => void
  handleAutoCompleteClose: () => void
}> = ({ suggestion, selectSuggestion, handleAutoCompleteClose }) => {
  const handleClick = useCallbackDebounced(() => {
    selectSuggestion(suggestion)
    handleAutoCompleteClose()
  }, [handleAutoCompleteClose, selectSuggestion, suggestion])
  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = useCallback(
    e => {
      if (e.key === 'Enter') {
        e.preventDefault()
        selectSuggestion(suggestion)
        handleAutoCompleteClose()
      }
    },
    [handleAutoCompleteClose, selectSuggestion, suggestion],
  )
  const handleMouseDown: React.MouseEventHandler<HTMLDivElement> = useCallback(e => {
    e.preventDefault()
  }, [])
  return (
    <Box
      className="Suggestion-Option"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseDown={handleMouseDown}
      tabIndex={0}
      sx={theme => ({
        cursor: 'pointer',
        paddingTop: 1,
        paddingBottom: 1,
        paddingLeft: 1,
        paddingRight: 1,
        '&:hover, &:focus': {
          backgroundColor: theme.palette.primary.main,
          outline: 'none', // optional: remove default ring
        },
      })}
    >
      <Typography variant={'body2'} sx={theme => ({ color: theme.palette.primary.contrastText })}>
        {suggestion.address}
      </Typography>
    </Box>
  )
}

// eslint-disable-next-line react/display-name
const AutoCompleteInput = forwardRef<
  HTMLInputElement,
  {
    onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> | undefined
    onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> | undefined
    value: string | undefined | null
    disabled?: boolean
    loading?: boolean
    loadingSelection?: boolean
    readOnly?: boolean
    error?: boolean
    name?: string
    suggestions: AutocompleteSuggestion[]
    selectSuggestion: (suggestion: AutocompleteSuggestion) => void
  }
>((props, outerRef) => {
  const ref = useRef<HTMLElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const suggestionsContainerRef = useRef<HTMLElement>(null)
  useImperativeHandle(outerRef, () => inputRef.current!, [])
  const {
    onChange,
    onBlur,
    value,
    disabled,
    readOnly,
    name,
    error,
    suggestions,
    selectSuggestion,
    loading,
    loadingSelection,
  } = props
  const [autoCompleteAnchorEl, setAutoCompleteAnchorEl] = useState<null | HTMLElement>(null)
  const [placement, setPlacement] = useState<'top' | 'bottom' | undefined>()
  const [maxHeight, setMaxHeight] = useState<number>(0)
  const trackPlacement = useMemo(
    () => ({
      name: 'trackPlacement',
      enabled: true,
      phase: 'afterWrite' as const,
      fn({ state }: { state: { placement: 'top' | 'bottom' } }) {
        setPlacement(state.placement)
        if (!autoCompleteAnchorEl) {
          return
        }
        const rect = autoCompleteAnchorEl.getBoundingClientRect()
        const distBottom = window.innerHeight - rect.bottom
        const distTop = rect.top
        if (state.placement === 'top') {
          setMaxHeight(distTop)
        } else {
          setMaxHeight(distBottom)
        }
      },
    }),
    [autoCompleteAnchorEl],
  )
  const handleAutoCompleteOpen = useCallback(() => {
    if (disabled || readOnly || loadingSelection) {
      return
    }
    setAutoCompleteAnchorEl(ref.current)
    ref.current?.scrollIntoView({ behavior: 'smooth' })
  }, [disabled, readOnly, loadingSelection])
  const handleAutoCompleteClose = useCallback(() => {
    setAutoCompleteAnchorEl(null)
    setPlacement(undefined)
  }, [])
  const handleBackdropClick: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> =
    useCallback(
      event => {
        setAutoCompleteAnchorEl(null)
        setPlacement(undefined)
        onBlur?.(event)
      },
      [onBlur],
    )
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab' && autoCompleteAnchorEl) {
      e.preventDefault()
      // find & focus the first option inside the Popper
      const first =
        suggestionsContainerRef.current?.querySelector<HTMLElement>('.Suggestion-Option')
      first?.focus()
    }
    setAutoCompleteAnchorEl(ref.current)
  }
  const handleOnBlur: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = useCallback(
    event => {
      const next = event.relatedTarget as HTMLElement | null
      if (next && suggestionsContainerRef.current?.contains(next)) {
        return
      }
      onBlur?.(event)
    },
    [onBlur],
  )
  return (
    <>
      <Backdrop
        open={!!autoCompleteAnchorEl}
        // @ts-expect-error focus event handler
        onClick={handleBackdropClick}
        sx={theme => ({
          zIndex: theme.zIndex.modal,
        })}
        invisible
      />
      <OutlinedInput
        className={autoCompleteAnchorEl ? 'Mui-focused' : undefined}
        ref={ref}
        inputRef={inputRef}
        autoComplete="do-not-autofill"
        autoCapitalize="off"
        autoCorrect="off"
        onChange={onChange}
        onBlur={handleOnBlur}
        onFocus={handleAutoCompleteOpen}
        value={value}
        disabled={disabled}
        readOnly={readOnly || loadingSelection}
        name={name}
        error={error && !autoCompleteAnchorEl}
        inputProps={{
          onKeyDown: handleKeyDown,
        }}
        sx={theme => ({
          scrollMarginTop: 8,
          paddingRight: '74px',
          ...(!!autoCompleteAnchorEl && {
            zIndex: theme.zIndex.modal + 1,
          }),
          ...(!!autoCompleteAnchorEl &&
            placement === 'bottom' && {
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            }),
          ...(!!autoCompleteAnchorEl &&
            placement === 'top' && {
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
            }),
        })}
        endAdornment={
          <Button
            tabIndex={-1}
            variant="contained"
            disabled={disabled || readOnly}
            loading={loadingSelection}
            sx={theme => ({
              position: 'absolute',
              top: 0,
              bottom: 0,
              right: 0,
              borderBottomLeftRadius: 0,
              borderTopLeftRadius: 0,
              boxShadow: 'none',
              ...(!!autoCompleteAnchorEl &&
                placement === 'bottom' && {
                  borderBottomRightRadius: 0,
                }),
              ...(!!autoCompleteAnchorEl &&
                placement === 'top' && {
                  borderTopRightRadius: 0,
                }),
              '&:hover': {
                boxShadow: 'none',
                backgroundColor: theme.palette.primary.main,
              },
              '&.MuiButton-loading': {
                backgroundColor: theme.palette.primary.main,
              },
              '& .MuiButton-loadingIndicator': {
                color: '#fff',
              },
            })}
            onFocus={handleAutoCompleteOpen}
          >
            <SearchIcon fontSize="small" />
          </Button>
        }
      />
      <Popper
        anchorEl={autoCompleteAnchorEl}
        open={!!autoCompleteAnchorEl}
        sx={{
          zIndex: 1300,
          width: autoCompleteAnchorEl?.clientWidth || 0,
        }}
        popperOptions={{
          modifiers: [trackPlacement],
        }}
      >
        {loading && <LinearProgress sx={{ position: 'absolute', top: 0, left: 2, right: 0 }} />}
        <Box
          ref={suggestionsContainerRef}
          tabIndex={-1}
          sx={{
            backgroundColor: 'primary.main',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            outline: 'none',
            ...(!!autoCompleteAnchorEl &&
              placement === 'bottom' && {
                borderBottomLeftRadius: 4,
                borderBottomRightRadius: 4,
                maxHeight: maxHeight - 1,
              }),
            ...(!!autoCompleteAnchorEl &&
              placement === 'top' && {
                borderTopLeftRadius: 4,
                borderTopRightRadius: 4,
                maxHeight: maxHeight - 1,
              }),
          }}
        >
          {!suggestions.length && (
            <Box sx={{ padding: 1, color: 'white' }}>
              <Typography
                variant={'body2'}
                sx={theme => ({ color: theme.palette.primary.contrastText })}
              >
                No results
              </Typography>
            </Box>
          )}
          {suggestions.map(suggestion => {
            return (
              <SuggestedResultRow
                key={suggestion.id}
                suggestion={suggestion}
                selectSuggestion={selectSuggestion}
                handleAutoCompleteClose={handleAutoCompleteClose}
              />
            )
          })}
        </Box>
      </Popper>
    </>
  )
})

export default AutoCompleteInput
